import { useContext, useEffect, useRef, useState } from "react";
import {
  IStepOrderStateChangeLog,
  IStepOrderTableReport,
  StepOrderTypes,
} from "../../../Interfaces/stepInterfaces";
import {
  IAdditional,
  IAdditionalHeaderProps,
} from "../../TableTemplate/tableTemplate";
import { toast } from "react-toastify";
import useRequest from "../../../Hooks/useRequest";
import {
  bulkPauseStepOrder,
  bulkResumeStepOrder,
  cancelStepOrder,
  getBrokerOrdersTable,
  getStepGraph,
  getStepOrderStateChangeHistory,
  getStepShekarChanceTable,
  pauseStepOrder,
  resumeStepOrder,
  stepBulkOrderCancel,
} from "../../../Requests/stepRequests";
import { Menu, MenuItem, TableCell } from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseIcon from "@mui/icons-material/Pause";
import DeleteIcon from "@mui/icons-material/Delete";
import { WrappedTimeSeriesChart } from "../../../Components/Timeseries/WrappedTimeSeriesChart";
import Modal from "../../../Components/Modal/Modal";
import Button from "../../../Components/Button";
import {
  OrderStatus,
  OrderStrategy,
} from "../../../Interfaces/orderInterfaces";
import TimelineIcon from "@mui/icons-material/Timeline";
import HuntStrategyChancesTable from "../../Order/HuntChances/HuntStrategyChancesTable";
import EditIcon from "@mui/icons-material/Edit";
import { useAppDispatch } from "../../../App/hooks";
import { setStepEditRowValues } from "../../../Features/Step/stepSlice";
import BrokerOrdersTable from "../BrokerOrder/BrokerOrdersTable";
import SubOrdersTable from "../SubOrder/SubOrdersTable";
import CompetitionChanceTable from "../CompetitionChange/CompetitionChanceTable";
import { formatStepScheduleCountdown } from "../../../Pages/Step/stepScheduleCountdown";
import { StepOrderPausePlayAcceptedContext } from "./StepOrderPausePlayContext";
import { StepOrderWarning } from "./StepOrderWarning";

const StepScheduleCountdown = ({ row }: { row: IStepOrderTableReport }) => {
  const responseReceivedAt = useRef(Date.now());
  const [elapsedMilliseconds, setElapsedMilliseconds] = useState(0);

  useEffect(() => {
    responseReceivedAt.current = Date.now();
    setElapsedMilliseconds(0);

    if (!row.nextScheduledAt) return;
    const timer = window.setInterval(
      () => setElapsedMilliseconds(Date.now() - responseReceivedAt.current),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [row.nextScheduledAt, row.serverNow]);

  const countdown = formatStepScheduleCountdown(
    row.nextScheduledAt,
    row.serverNow,
    elapsedMilliseconds,
  );
  if (countdown === "-") return <>-</>;

  return (
    <span className="whitespace-nowrap">
      {row.nextScheduledAction === "Start" ? "تا شروع" : "تا توقف"} {countdown}
    </span>
  );
};

const StepOrderTableAdditionalHeader = ({
  selectedRowsIds,
  setSelectedRowsIds,
}: IAdditionalHeaderProps) => {
  const onPausePlayAccepted = useContext(StepOrderPausePlayAcceptedContext);
  const bulkPausePlayInFlight = useRef(false);
  const bulkRequestStartedAt = useRef<number | null>(null);
  const onError = (e: any) => {
    if (e?.response?.data?.value) {
      toast.error(e.response.data.value);
      return;
    }
    toast.error(e?.response?.status === 500 ? "خطا داخلی" : "خطا در ارتباط با سرور");
  };

  const bulkCancelOrderRequest = useRequest(
    () => {
      const ids = [...selectedRowsIds];
      return stepBulkOrderCancel(ids).then((response) => {
        if (!response.data) {
          toast.error(response.msg || "درخواست حذف پذیرفته نشد");
          return response;
        }
        onPausePlayAccepted(ids, performance.now(), true);
        toast.info("درخواست حذف ثبت شد");
        return response;
      });
    },
    onError
  );
  const bulkPauseOrderRequest = useRequest(
    (signal) => {
      const ids = [...selectedRowsIds];
      return bulkPauseStepOrder(ids, signal).then((response) => {
        if (!response.data) {
          toast.error(response.msg || "درخواست پاز پذیرفته نشد");
          return response;
        }
        const acceptedAt = performance.now();
        console.info("StepOrder Pause/Play UI timing", {
          StepOrderIds: ids,
          Action: "Pause",
          Stage: "ClickToAccepted",
          ElapsedMs: acceptedAt - (bulkRequestStartedAt.current ?? acceptedAt),
        });
        onPausePlayAccepted(ids, acceptedAt);
        return response;
      }).finally(() => {
        bulkPausePlayInFlight.current = false;
      });
    },
    onError
  );
  const bulkResumeOrderRequest = useRequest(
    (signal) => {
      const ids = [...selectedRowsIds];
      return bulkResumeStepOrder(ids, signal).then((response) => {
        if (!response.data) {
          toast.error(response.msg || "درخواست پلی پذیرفته نشد");
          return response;
        }
        const acceptedAt = performance.now();
        console.info("StepOrder Pause/Play UI timing", {
          StepOrderIds: ids,
          Action: "Play",
          Stage: "ClickToAccepted",
          ElapsedMs: acceptedAt - (bulkRequestStartedAt.current ?? acceptedAt),
        });
        onPausePlayAccepted(ids, acceptedAt);
        return response;
      }).finally(() => {
        bulkPausePlayInFlight.current = false;
      });
    },
    onError
  );
  const isBulkPausePlayLoading =
    bulkPauseOrderRequest.loading || bulkResumeOrderRequest.loading;

  const bulkCancelOrderHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length || bulkCancelOrderRequest.loading) return;
    bulkCancelOrderRequest.fetchData();
    setSelectedRowsIds([]);
  };

  const bulkPauseOrderHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length || bulkPausePlayInFlight.current) return;
    bulkPausePlayInFlight.current = true;
    bulkRequestStartedAt.current = performance.now();
    bulkPauseOrderRequest.fetchData();
  };

  const bulkPlayOrderHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length || bulkPausePlayInFlight.current) return;
    bulkPausePlayInFlight.current = true;
    bulkRequestStartedAt.current = performance.now();
    bulkResumeOrderRequest.fetchData();
  };

  return (
    <>
      <TableCell>
        <span className="!font-black text-black">هشدار</span>
      </TableCell>
      <TableCell>
        <span className="!font-black text-black">زمان باقی‌مانده</span>
      </TableCell>
      <TableCell>
        <div className="flex flex-row">
          <div
            onClick={
              selectedRowsIds.length && !isBulkPausePlayLoading
                ? bulkPauseOrderHandler
                : undefined
            }
            className={`transition-all rounded p-1 ${
              isBulkPausePlayLoading
                ? "pointer-events-none opacity-50"
                : "hover:bg-slate-200"
            }`}
          >
            <PauseIcon
              className={`text-sky-900 cursor-pointer  ${
                selectedRowsIds.length && !isBulkPausePlayLoading
                  ? "text-sky-900 "
                  : "text-gray-300"
              } transition-all`}
            />
          </div>
          <div
            onClick={
              selectedRowsIds.length && !isBulkPausePlayLoading
                ? bulkPlayOrderHandler
                : undefined
            }
            className={`transition-all rounded p-1 ${
              isBulkPausePlayLoading
                ? "pointer-events-none opacity-50"
                : "hover:bg-slate-200"
            }`}
          >
            <PlayCircleIcon
              className={`text-sky-900 cursor-pointer ${
                selectedRowsIds.length && !isBulkPausePlayLoading
                  ? "text-sky-900 "
                  : "text-gray-300"
              } transition-all`}
            />
          </div>
        </div>
      </TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell>
        <div
          className={`hover:bg-red-100 transition-all rounded p-1 ${bulkCancelOrderRequest.loading ? "pointer-events-none opacity-50" : ""}`}
          onClick={selectedRowsIds.length && !bulkCancelOrderRequest.loading ? bulkCancelOrderHandler : undefined}
        >
          <DeleteIcon
            fontSize="small"
            className={`cursor-pointer ${
              selectedRowsIds.length && !bulkCancelOrderRequest.loading ? "text-red-900 " : "text-gray-300"
            } transition-all`}
          />
        </div>
      </TableCell>
    </>
  );
};

const StepOrderTableAdditionalCol: React.FC<{
  row: IStepOrderTableReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const onPausePlayAccepted = useContext(StepOrderPausePlayAcceptedContext);
  const serverNowReceivedAt = useRef(Date.now());
  const [isChancesOpen, setIsChancesOpen] = useState(false);
  const [isSahraOrderTableOpen, setIsSahraOrderTableOpen] = useState(false);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const [isCompetitionChanceOpen, setIsCompetitionChanceOpen] = useState(false);
  const [isBrokerOrdersModalOpen, setIsBrokerOrdersModalOpen] = useState(false);
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState<HTMLElement | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<IStepOrderStateChangeLog[]>([]);
  const [isPausePlayPending, setIsPausePlayPending] = useState(false);
  const [isDeleteRequesting, setIsDeleteRequesting] = useState(false);
  const [commandAcceptedAt, setCommandAcceptedAt] = useState<number | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    serverNowReceivedAt.current = Date.now();
  }, [row.serverNow]);

  useEffect(() => {
    if (
      isPausePlayPending &&
      commandAcceptedAt !== null &&
      row.pausePlayStatusRequestedAt !== undefined &&
      row.pausePlayStatusRequestedAt >= commandAcceptedAt
    ) {
      setIsPausePlayPending(false);
      setCommandAcceptedAt(null);
    }
  }, [
    isPausePlayPending,
    commandAcceptedAt,
    row.pausePlayStatusRequestedAt,
  ]);

  const orderGraphRequest = useRequest(
    () => getStepGraph(JSON.parse(row.getGraphStr)),
    errorHandler
  );

  function errorHandler(e: any) {
    let error: any;

    if (!e) error = "خطا در ارتباط با سرور";
    if (e.msg) error = e.msg;
    if (e?.response?.data?.value) error = e.response.data.value;
    toast.error(error);
  }

  const btnClickHandler = () => {
    setIsChancesOpen(true);
  };

  const cancelOrderHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    if (isDeleteRequesting || row.deletePending) return;
    setIsDeleteRequesting(true);
    cancelStepOrder(row.id)
      .then((resp) => {
        if (!resp.data) {
          errorHandler({ msg: resp.msg || "درخواست حذف پذیرفته نشد" });
          return;
        }
        onPausePlayAccepted([row.id], performance.now(), true);
        toast.info("درخواست حذف ثبت شد");
      })
      .catch((e) => errorHandler(e))
      .finally(() => setIsDeleteRequesting(false));
  };

  const pauseOrderHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPausePlayPending || row.pausePlayDisabled) return;
    setIsPausePlayPending(true);
    setCommandAcceptedAt(null);
    const requestStartedAt = performance.now();
    pauseStepOrder(row.id)
      .then((response) => {
        if (!response.data) {
          setIsPausePlayPending(false);
          errorHandler({ msg: response.msg || "درخواست پاز پذیرفته نشد" });
          onAction();
          return;
        }
        const acceptedAt = performance.now();
        console.info("StepOrder Pause/Play UI timing", {
          StepOrderId: row.id,
          Action: "Pause",
          Stage: "ClickToAccepted",
          ElapsedMs: acceptedAt - requestStartedAt,
        });
        setCommandAcceptedAt(acceptedAt);
        onPausePlayAccepted([row.id], acceptedAt);
      })
      .catch((e) => {
        setIsPausePlayPending(false);
        setCommandAcceptedAt(null);
        errorHandler(e);
      });
  };

  const resumeOrder = () => {
    if (isPausePlayPending || row.pausePlayDisabled) return;
    setIsPausePlayPending(true);
    setCommandAcceptedAt(null);
    const requestStartedAt = performance.now();
    resumeStepOrder(row.id)
      .then((response) => {
        if (!response.data) {
          setIsPausePlayPending(false);
          errorHandler({ msg: response.msg || "درخواست پلی پذیرفته نشد" });
          onAction();
          return;
        }
        const acceptedAt = performance.now();
        console.info("StepOrder Pause/Play UI timing", {
          StepOrderId: row.id,
          Action: "Play",
          Stage: "ClickToAccepted",
          ElapsedMs: acceptedAt - requestStartedAt,
        });
        setCommandAcceptedAt(acceptedAt);
        onPausePlayAccepted([row.id], acceptedAt);
      })
      .catch((e) => {
        setIsPausePlayPending(false);
        setCommandAcceptedAt(null);
        errorHandler(e);
      });
  };

  const playOrderHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    resumeOrder();
  };

  const pausePlayContextMenuHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setHistoryMenuAnchor(e.currentTarget);
  };

  const showHistoryHandler = () => {
    setHistoryMenuAnchor(null);
    setIsHistoryOpen(true);
    setHistoryLoading(true);
    getStepOrderStateChangeHistory(row.id)
      .then(setHistory)
      .catch(errorHandler)
      .finally(() => setHistoryLoading(false));
  };

  const getHistoryModalUI = () => (
    <div className="w-full px-8 py-4" dir="rtl">
      <h2 className="mb-5 text-lg font-bold">تاریخچه توقف و اجرای سفارش</h2>
      {historyLoading ? (
        <div className="py-10 text-center">در حال دریافت اطلاعات...</div>
      ) : (
        <div className="max-h-[65vh] overflow-auto">
          <table className="w-full min-w-[750px] border-collapse text-center">
            <thead className="sticky top-0 bg-slate-100">
              <tr>
                <th className="border p-3">نام اکانت</th>
                <th className="border p-3">نماد</th>
                <th className="border p-3">عامل تغییر</th>
                <th className="border p-3">از وضعیت</th>
                <th className="border p-3">به وضعیت</th>
                <th className="border p-3">زمان تغییر</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item.id} className="odd:bg-white even:bg-slate-50">
                  <td className="border p-3">{item.accountName}</td>
                  <td className="border p-3">{item.symbol}</td>
                  <td className="border p-3">{item.actor}</td>
                  <td className="border p-3">{item.fromState}</td>
                  <td className="border p-3">{item.toState}</td>
                  <td className="border p-3 whitespace-nowrap">{item.changedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const getOrderGraphHandler = () => {
    orderGraphRequest.fetchData();
  };
  useEffect(() => {
    if (!orderGraphRequest.response) return;
    setIsGraphModalOpen(true);
  }, [orderGraphRequest.response]);

  const getGraphModalUI = () => {
    if (!orderGraphRequest.response) return null;
    return (
      <div className="chart-fill-modal">
        <WrappedTimeSeriesChart dataset={orderGraphRequest.response} className="h-full w-full" />
      </div>
    );
  };

  const editHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setStepEditRowValues({ ...row }));
  };

  return (
    <>
      <TableCell>
        {row.pausePlayWarning || row.deleteWarning || row.editWarning ? (
          <StepOrderWarning
            warnings={[row.pausePlayWarning, row.deleteWarning, row.editWarning]}
            editWarning={row.editWarning}
            retryDisabled={Boolean(row.editDisabled || row.pausePlayPending || row.deletePending
              || isPausePlayPending || isDeleteRequesting)}
            onRetryEdit={() => dispatch(setStepEditRowValues({ ...row }))}
          />
        ) : null}
      </TableCell>
      <TableCell>
        <StepScheduleCountdown row={row} />
      </TableCell>
      <TableCell onContextMenu={pausePlayContextMenuHandler}>
        <Menu
          anchorEl={historyMenuAnchor}
          open={Boolean(historyMenuAnchor)}
          onClose={() => setHistoryMenuAnchor(null)}
        >
          <MenuItem onClick={showHistoryHandler}>نمایش لاگ</MenuItem>
        </Menu>
        {isHistoryOpen ? (
          <Modal
            UI={getHistoryModalUI()}
            onClose={() => setIsHistoryOpen(false)}
            modalBoxClassName="!w-[90vw] !max-w-[1100px]"
          />
        ) : null}
        <div
          className={`flex flex-col items-center leading-none ${
            isPausePlayPending || row.pausePlayDisabled
              ? "pointer-events-none opacity-50"
              : ""
          }`}
        >
          {row.isActive || row.pauseRetryRequired ? (
            <div className="hover:bg-slate-300 transition-all rounded p-1">
              <PauseIcon
                className="text-sky-700 cursor-pointer"
                onClick={pauseOrderHandler}
              />
            </div>
          ) : null}
          {row.isActive === false && !row.pauseRetryRequired ? (
            <div className="hover:bg-slate-300 transition-all rounded p-1">
              <PlayCircleIcon
                className="text-sky-700 cursor-pointer"
                onClick={playOrderHandler}
              />
            </div>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <div
          className={`transition-all rounded p-1 ${
            row.editDisabled
              ? "pointer-events-none opacity-40"
              : "hover:bg-slate-300"
          }`}
          onClick={row.editDisabled ? undefined : editHandler}
          title={row.editDisabled ? "تا تعیین تکلیف پاز/پلی امکان ویرایش وجود ندارد" : undefined}
        >
          <EditIcon className="text-sky-700 cursor-pointer" />
        </div>
      </TableCell>
      <TableCell>
        {isBrokerOrdersModalOpen ? (
          <Modal
            UI={
              <BrokerOrdersTable
                getData={getBrokerOrdersTable}
                stepOrderId={row.id}
              />
            }
            onClose={() => setIsBrokerOrdersModalOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsBrokerOrdersModalOpen(true)}
          text="سفارشات"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        {isSahraOrderTableOpen ? (
          <Modal
            UI={<SubOrdersTable stepOrderId={row.id} />}
            onClose={() => setIsSahraOrderTableOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsSahraOrderTableOpen(true)}
          text="جزئیات"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        {isChancesOpen ? (
          <Modal
            UI={
              <HuntStrategyChancesTable
                getData={(_, offset, pageSize) =>
                  getStepShekarChanceTable(
                    { stepOrderId: row.id },
                    offset,
                    pageSize
                  )
                }
              />
            }
            onClose={() => setIsChancesOpen(false)}
          />
        ) : null}
        {row.orderStartegy === OrderStrategy.Hunt ? (
          <Button
            onClick={btnClickHandler}
            text="شانس ها"
            variant="contained"
            className="table-btn"
          />
        ) : null}
        {row.orderStartegy === OrderStrategy.StepCompetition ? (
          <>
            {isCompetitionChanceOpen ? (
              <Modal
                onClose={() => setIsCompetitionChanceOpen(false)}
                UI={<CompetitionChanceTable stepOrderId={row.id} />}
                fullScreen
              />
            ) : null}
            <Button
              onClick={() => setIsCompetitionChanceOpen(true)}
              text="شانس"
              variant="contained"
              className="table-btn"
            />
          </>
        ) : (
          <></>
        )}
      </TableCell>
      <TableCell>
        {row.orderStartegy === OrderStrategy.Hunt ? (
          <>
            {isGraphModalOpen ? (
              <Modal
                onClose={() => setIsGraphModalOpen(false)}
                UI={getGraphModalUI()}
                fullScreen
              />
            ) : null}
            <Button
              onClick={getOrderGraphHandler}
              text={<TimelineIcon className="!text-base" />}
              variant="contained"
              className="table-btn"
            />
          </>
        ) : (
          <></>
        )}
      </TableCell>
      <TableCell>
        {row.orderStatus !== OrderStatus.Cancelled || row.deletePending || row.deleteWarning ? (
          <div
            onClick={isDeleteRequesting || row.deletePending ? undefined : cancelOrderHandler}
            className={`hover:bg-red-100 transition-all rounded p-1 ${isDeleteRequesting || row.deletePending ? "pointer-events-none opacity-50" : ""}`}
          >
            <DeleteIcon
              fontSize="small"
              className="text-red-700 cursor-pointer"
            />
          </div>
        ) : null}
      </TableCell>
    </>
  );
};

export const StepOrderTableAdditional: IAdditional<IStepOrderTableReport> = {
  getHeader: StepOrderTableAdditionalHeader,
  getRow: StepOrderTableAdditionalCol,
};
